"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import {
  Flex,
  Container,
  IconButton,
  Box,
  Text,
} from "@chakra-ui/react";
import Image from "next/image";
import Link from "next/link";

// Components
import { DesktopNavLinks } from "./DesktopNavLinks";
import { DesktopUserMenu } from "./DesktopUserMenu";
import MobileLeftSidebar from "./MobileLeftSidebar";
import MobileRightSidebar from "./MobileRightSidebar";

// Style 
import { HeaderStyles, LoginPromptHeaderStyles } from "styles/headerStyles";

// Icons
import { User, Menu, Mail, CheckCircle, Copy } from 'lucide-react';
import { isAuthenticated } from "middlewares/auth";
import { useUnreadNotifications } from "hooks/useUnreadNotifications";

const CounterBadge = ({ count }: { count: number }) => {
  if (count <= 0) return null;

  return (
    <Flex
      align="center"
      justify="center"
      minW="18px"
      h="18px"
      px="1"
      bg="red.600"
      color="white"
      borderRadius="full"
      fontSize="10px"
      fontWeight="bold"
      lineHeight="1"
    >
      {count > 99 ? "99+" : count}
    </Flex>
  );
};

export default function Header() {
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const [consultCode, setConsultCode] = useState<string | null>(null);
  const { unreadCount } = useUnreadNotifications();

useEffect(() => {
    setIsUserAuthenticated(isAuthenticated());
    
    const carregarCodigo = () => {
      const code = localStorage.getItem("consultCode");
      setConsultCode(code);
    };

    carregarCodigo();

    window.addEventListener("storage", carregarCodigo);

    return () => window.removeEventListener("storage", carregarCodigo);
  }, []);

  // useEffect(() => {
  //   setIsUserAuthenticated(isAuthenticated());
  //   const code = localStorage.getItem("consultCode");
  //   setConsultCode(code);
  // }, []);

  // Scroll effect
  const handleScroll = useCallback(() => {
    const scrollPosition = window.scrollY;
    const shouldBeScrolled = scrollPosition > 1;
    setIsScrolled(shouldBeScrolled);
  }, []);

  useEffect(() => {
    const throttledHandleScroll = () => {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      scrollTimeout.current = setTimeout(handleScroll, 1);
    };

    handleScroll();
    window.addEventListener("scroll", throttledHandleScroll);

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [handleScroll]);

  return (
    <>
      <Container
        {...HeaderStyles.container}
        opacity={[1, null, isScrolled ? 0.95 : 1]}
        transition="all 0.4s ease-in-out"
      >
        <Box>
          <Box
            {...LoginPromptHeaderStyles.container}
            transition="all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
            bg={isScrolled ? "white" : "red-wine"}
            maxHeight={
              isScrolled
                ? "0px"
                : isUserAuthenticated
                  ? ["0px", "80px"]
                  : "80px"
            }
            opacity={
              isScrolled
                ? 0
                : isUserAuthenticated
                  ? [0, 1]
                  : 1
            }
            filter={
              isScrolled
                ? "blur(5px)"
                : isUserAuthenticated
                  ? ["blur(5px)", "blur(0px)"]
                  : "blur(0px)"
            }
            transform={
              isScrolled
                ? "translateY(-15px)"
                : isUserAuthenticated
                  ? ["translateY(-15px)", "translateY(0)"]
                  : "translateY(0)"
            }
            overflow="hidden"
            py={
              isScrolled
                ? 0
                : isUserAuthenticated
                  ? [0, 2]
                  : 2
            }
            visibility={
              isScrolled
                ? "hidden"
                : isUserAuthenticated
                  ? ["hidden", "visible"]
                  : "visible"
            }
          >
            <Container maxW="85rem">
              <Flex
                {...LoginPromptHeaderStyles.contactWrapper}
                pl={isUserAuthenticated ? 20 : 0}
              >
                {!isUserAuthenticated && (
                  <Link href="/conta/login">
                    <Flex {...LoginPromptHeaderStyles.textBox}
                      transition="all 0.2s ease"
                      _hover={{
                        transform: "scale(1.01)",
                        color: "red.400"
                      }}>
                      <Text {...LoginPromptHeaderStyles.text}>
                        Olá, Faça seu login ou cadastre-se
                      </Text>
                    </Flex>
                  </Link>
                )}
                <Link href="/consultar-associado">
                  <Flex {...LoginPromptHeaderStyles.contactItem} ml={[0, "0px"]}
                    transition="all 0.2s ease"
                    _hover={{
                      transform: "scale(1.01)",
                      color: "red.400"
                    }}>
                    <CheckCircle size={14} />
                    <Text
                      {...LoginPromptHeaderStyles.contactText}
                      textAlign="center"
                    >
                      Consulta associado
                    </Text>
                  </Flex>
                </Link>
                {isUserAuthenticated && consultCode && (
                  <Flex
                    {...LoginPromptHeaderStyles.contactItem} align="center">
                    <Text {...LoginPromptHeaderStyles.text}>
                      Meu Código Associado:
                    </Text>

                    <Link
                      href={`/consultar-associado?code=${encodeURIComponent(consultCode)}`}
                      passHref
                    >
                      <Flex
                        gap="1"
                        align="center"
                        cursor="pointer"
                        transition="all 0.2s ease"
                        _hover={{
                          transform: "scale(1.01)",
                          color: "red.400"
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(consultCode);
                        }}>
                        <Copy size={14} />
                        <Text {...LoginPromptHeaderStyles.text}>
                          {consultCode}
                        </Text>
                      </Flex>
                    </Link>
                  </Flex>
                )}
                <Flex align="center" gap={2}>
                  <Mail size={14} />
                  <Link href="mailto:contato@aajump.com.br" passHref>
                    <Text
                      {...LoginPromptHeaderStyles.contactText}
                      textAlign="center"
                      style={{ cursor: 'pointer' }}
                    >
                      contato@aajump.com.br
                    </Text>
                  </Link>
                </Flex>
              </Flex>
            </Container>
          </Box>
        </Box>

        {/* Main Navigation */}
        <Container maxW="85rem" mx="auto">
          <Flex
            as="nav"
            {...HeaderStyles.nav}
            py={[0, null, isScrolled ? "10px" : "20px"]}
            transform={isScrolled ? "translateY(-2px)" : "translateY(0)"}
          >
            <IconButton
              {...HeaderStyles.mobileMenuButton}
              onClick={() => setIsMenuOpen(true)}
            >
              <Menu size={24} />
            </IconButton>
            <Link href="/">
              <Box
                {...HeaderStyles.logoBox}
                transition="all 0.4s ease-in-out"
                height={isScrolled ? ["55px", "65px"] : ["60px", "70px"]}
                width={isScrolled ? ["255px", "275px"] : ["260px", "280px"]}>
                <Image
                  src="/images/logos/aajumpLogoFull.webp"
                  alt="AAJump Logo"
                  width={457}
                  height={114}
                  style={{ objectFit: "contain" }}
                  priority
                />
              </Box>
            </Link>

            <Box {...HeaderStyles.desktopNavLinks}>
              <DesktopNavLinks />
            </Box>

            <IconButton
              {...HeaderStyles.mobileUserButton}
              onClick={() => setIsUserMenuOpen(true)}
              position="relative"
              overflow="visible"
            >
              <User size={16} />
              {isUserAuthenticated && unreadCount > 0 && (
                <Box position="absolute" top="1" right="1">
                  <CounterBadge count={unreadCount} />
                </Box>
              )}
            </IconButton>

            <Box {...HeaderStyles.desktopUserMenu}>
              <DesktopUserMenu />
            </Box>
          </Flex>
        </Container>
      </Container>

      {/* Sidebars */}
      <MobileLeftSidebar
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
      />
      <MobileRightSidebar
        isOpen={isUserMenuOpen}
        onClose={() => setIsUserMenuOpen(false)}
      />
    </>
  );
}
